import { ref } from 'vue';

const globalBaseUrl = ref('');
const globalHeaders = ref({});
const requestInterceptors = ref<
  ((url: string, request: RequestInit) => RequestInit | Promise<RequestInit>)[]
>([]);
const responseInterceptors = ref<
  ((url: string, request: RequestInit, response: Response) => Response | Promise<Response>)[]
>([]);

const getBody = <T>(c: Response | Request): Promise<T> => {
  const contentType = c.headers.get('content-type');

  if (contentType && contentType.includes('application/json')) {
    return c.json();
  }

  if (contentType && contentType.includes('application/pdf')) {
    return c.blob() as Promise<T>;
  }

  return c.text() as Promise<T>;
};

const getUrl = (contextUrl: string): string => {
  const baseUrl = /https?:\/\//.test(globalBaseUrl.value)
    ? globalBaseUrl.value
    : `${window.location.origin}${globalBaseUrl.value}`;
  const requestUrl = new URL(baseUrl + contextUrl);

  return requestUrl.toString();
};

const getHeaders = (headers?: HeadersInit): HeadersInit => {
  return {
    ...globalHeaders.value,
    ...headers,
  };
};

interface CustomRequestInit extends RequestInit {
  /** The caller's own headers, before the global ones were merged in - a retry rebuilds from them */
  callHeaders?: HeadersInit;
  isRetry?: boolean;
}

const send = async (requestUrl: string, init: CustomRequestInit): Promise<Response> => {
  let requestInit: RequestInit = init;

  for (const interceptor of requestInterceptors.value) {
    requestInit = (await interceptor(requestUrl, requestInit)) ?? requestInit;
  }

  const request = new Request(requestUrl, requestInit);
  let response = await fetch(request);

  for (const interceptor of responseInterceptors.value) {
    response = (await interceptor(requestUrl, requestInit, response)) ?? response;
  }

  return response;
};

export const customFetch = async <T>(url: string, options: RequestInit): Promise<T> => {
  const response = await send(getUrl(url), {
    ...options,
    headers: getHeaders(options.headers),
    callHeaders: options.headers,
  });

  const data = await getBody<T>(response);

  return { status: response.status, data } as T;
};

/**
 * Sends a request again from a response interceptor - after a token refresh - and resolves to the
 * response the interceptor has to return. The headers are rebuilt from the current global ones,
 * since the original request still carries the expired token. A retry is never retried itself.
 */
export const retryRequest = (requestUrl: string, request: RequestInit) =>
  send(requestUrl, {
    ...request,
    headers: getHeaders((request as CustomRequestInit).callHeaders),
    isRetry: true,
  });

export const isRetriedRequest = (request: RequestInit) => !!(request as CustomRequestInit).isRetry;

export const useAcquisitionStoreServer = (baseUrl?: string) => {
  if (baseUrl) globalBaseUrl.value = baseUrl;

  const setBaseUrl = (url: string) => (globalBaseUrl.value = url);
  const setHeaders = (headers: HeadersInit) => (globalHeaders.value = headers);
  const addRequestInterceptor = (
    interceptor: (url: string, request: RequestInit) => RequestInit | Promise<RequestInit>,
  ) => requestInterceptors.value.push(interceptor);
  const addResponseInterceptor = (
    interceptor: (
      url: string,
      request: RequestInit,
      response: Response,
    ) => Response | Promise<Response>,
  ) => responseInterceptors.value.push(interceptor);

  return {
    setBaseUrl,
    setHeaders,
    addRequestInterceptor,
    addResponseInterceptor,
  };
};
